import { useState } from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import ContentBox from "../../../actprep-ui/components/ContentBox";
import layout from "../../../actprep-ui/constants/layout";
import ToDoList from "../../../actprep-ui/components/ToDoList";
import AdminProgramSchedule from "../../../components/AdminProgramSchedule";
import TimeTracker from "../../../actprep-ui/components/TimeTracker";
import { useGet } from "../../../actprep-ui/hooks/api";
import { apiUrls } from "../../../actprep-ui/utils/urls";
import {
  Badge,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import api from "../../../actprep-ui/utils/api";
import {
  AssignmentInd,
  Check,
  List as ListIcon,
  People,
} from "@mui/icons-material";
import WeekStats from "../../../actprep-ui/components/WeekStats";
import { useProfile } from "../../../actprep-ui/context/ProfileContext";
import useDisclosure from "../../../actprep-ui/hooks/ui/useDisclosure";
import EmployeeCoverageManagement from "../../../components/EmployeeCoverageManagement";
import RecentTimeEvents from "../../../actprep-ui/components/RecentTimeEvents";
import OnlineEmployeesDialog from "../../../components/OnlineEmployeesDialog";

const TOP_ROW_HEIGHT = "400px";

export default function Dashboard() {
  const [impersonation, setImpersonation] = useState(null);
  const {
    data: checkInData,
    refetch: refetchCheckinStatus,
    isLoading: isLoadingCheckinStatus,
  } = useGet(apiUrls.clock.checkIn());
  const { profile } = useProfile();
  const checkedIn = checkInData?.data.checked_in;
  const handleCheckIn = async () => {
    const response = await api.post(apiUrls.clock.checkIn());
    if (response.status == 200) {
      refetchCheckinStatus();
    }
  };
  const employeeCoverageDisclosure = useDisclosure();
  const clockEventsDisclosure = useDisclosure();
  const onlineEmployeesDisclosure = useDisclosure();

  const { data: delegationRequestsData, refetch: refetchDelegationData } =
    useGet(apiUrls.admin.employeeCoverage(), {
      params: {
        search_params: {
          coverer__user_profile__uuid: profile.uuid,
          status: "pending",
        },
        count: true,
      },
      enabled: !!profile,
    });
  const { data: onlineEmployeesData } = useGet(apiUrls.admin.adminProfiles(), {
    params: {
      search_params: {
        user_profile__is_clocked_in: true,
      },
      count: true,
    },
  });
  const totalOnlineEmployees = onlineEmployeesData?.data.count ?? 0;
  const totalPendingCoverageRequests = delegationRequestsData?.data.count ?? 0;
  return (
    <>
      <Dialog
        {...employeeCoverageDisclosure.getDisclosureProps()}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delegation Management</DialogTitle>
        <DialogContent>
          <EmployeeCoverageManagement onUpdate={refetchDelegationData} />
        </DialogContent>
      </Dialog>
      <OnlineEmployeesDialog
        {...onlineEmployeesDisclosure.getDisclosureProps()}
      />
      <Dialog
        {...clockEventsDisclosure.getDisclosureProps()}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Clock Events</DialogTitle>
        <DialogContent>
          <Box sx={{ height: "400px" }}>
            <RecentTimeEvents />
          </Box>
        </DialogContent>
      </Dialog>
      <Box
        sx={{
          my: `${layout.navBarHeight + 1}rem`,
          width: "100%",
        }}
        size={12}
      >
        <Typography variant="h5" fontWeight={"bold"} my={2} color={"primary"}>
          Dashboard{" "}
          {isLoadingCheckinStatus ? (
            <Chip size="small" sx={{ width: "100px" }} />
          ) : (
            <Chip
              label={checkedIn ? "Checked In" : "Check In"}
              color={checkedIn ? "success" : "info"}
              onClick={checkedIn ? undefined : handleCheckIn}
              icon={checkedIn ? <Check /> : undefined}
              size="small"
            />
          )}
        </Typography>
        <Grid container spacing={1}>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <ContentBox sx={{ height: "100%", maxHeight: TOP_ROW_HEIGHT }}>
              <TimeTracker />
              <Typography variant="overline" gutterBottom>
                Quick Actions
              </Typography>
              <Grid container spacing={1} sx={{ width: "100%" }} p={1}>
                <Grid size={6}>
                  <Button
                    startIcon={<ListIcon />}
                    variant="outlined"
                    size="small"
                    fullWidth
                    {...clockEventsDisclosure.getButtonProps()}
                  >
                    Clock events
                  </Button>
                </Grid>
                <Grid size={6}>
                  <Badge
                    badgeContent={totalPendingCoverageRequests}
                    overlap="circular"
                    variant="standard"
                    color="error"
                    sx={{
                      "& .MuiBadge-badge": {
                        right: 0,
                        top: 0,
                      },
                      width: "100%",
                    }}
                  >
                    <Button
                      startIcon={<AssignmentInd />}
                      variant="outlined"
                      size="small"
                      fullWidth
                      {...employeeCoverageDisclosure.getButtonProps()}
                    >
                      Delegate
                    </Button>
                  </Badge>
                </Grid>
                <Grid size={6}>
                  <Button
                    startIcon={<People />}
                    variant="outlined"
                    size="small"
                    fullWidth
                    {...onlineEmployeesDisclosure.getButtonProps()}
                  >
                    Who{"'"}s Online ({totalOnlineEmployees})
                  </Button>
                </Grid>
              </Grid>
            </ContentBox>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <ContentBox sx={{ height: "100%", maxHeight: TOP_ROW_HEIGHT }}>
              <AdminProgramSchedule
                impersonation={impersonation}
                onImpersonationChange={setImpersonation}
              />
            </ContentBox>
          </Grid>
          <Grid
            size={{
              xs: 12,
            }}
          >
            <ContentBox sx={{ height: "100%", maxHeight: "800px" }}>
              <ToDoList
                impersonation={impersonation}
                onImpersonationChange={setImpersonation}
              />
            </ContentBox>
          </Grid>
          <Grid
            size={{
              xs: 12,
            }}
          >
            <ContentBox sx={{ height: "100%", maxHeight: TOP_ROW_HEIGHT }}>
              <WeekStats />
            </ContentBox>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}
